<script setup lang="ts">
import type { DashboardRow } from '../../models/layout.types'

import { useI18n } from '@/shared/i18n'
import { Button, Icon } from '@/shared/ui'

defineOptions({ name: 'GridRowHeader' })

/**
 * A nested section's edit-mode header, as v3 drew it (`CustomDashboardSection.vue:14-32`): an
 * always-editable name behind a pencil, and a delete. Moving lives on the block overlay, like
 * v3's "Move" button, so the header carries no grip.
 */
const props = defineProps<{ row: DashboardRow }>()
const emit = defineEmits<{ rename: [name: string]; remove: [] }>()
const { t } = useI18n()

/** An unchanged name is not a write: a stray blur must not push a no-op onto the save funnel. */
function commit(value: string): void {
  const name = value.trim()
  if (name !== (props.row.name ?? '')) emit('rename', name)
}
</script>

<template>
  <header class="grid-row-header flex items-center justify-between gap-sm px-2xs pb-xs">
    <label
      class="grid-row-header__name group/name flex min-w-0 max-w-80 flex-auto cursor-text items-center gap-xs"
    >
      <Icon
        name="pencil"
        :size="14"
        class="shrink-0 text-muted transition-colors group-focus-within/name:text-primary"
        aria-hidden="true"
      />
      <input
        class="grid-row-header__input min-w-0 flex-auto border-0 bg-transparent p-0 font-sans text-lg font-semibold text-fg outline-none placeholder:text-subtle"
        type="text"
        :value="row.name ?? ''"
        :placeholder="t('dashboards.grid.sectionName')"
        :aria-label="t('dashboards.grid.renameSection')"
        :data-testid="`dashboard-rename-section-${row.id}`"
        @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
        @blur="commit(($event.target as HTMLInputElement).value)"
      />
    </label>
    <Button
      variant="soft"
      size="sm"
      square
      :aria-label="t('dashboards.grid.removeSection')"
      :title="t('dashboards.grid.removeSection')"
      data-testid="dashboard-remove-section"
      @click="emit('remove')"
    >
      <Icon name="trash" :size="13" />
    </Button>
  </header>
</template>
